import React from "react";
import { Alert, Form, Modal, Table } from "react-bootstrap";

import ConditionalButton from "../../../../layout/conditional-button";

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "(empty)";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const MODEL_LABELS = {
    Account: "Account",
    Address: "Address",
    Attorney: "Attorney profile",
    AttorneyAdmission: "Attorney admission",
    Availability: "Availability",
    Biography: "Biography profile",
    BankReconciliation: "Bank reconciliation",
    BankReconciliationLine: "Bank reconciliation line",
    BoardTask: "Board task",
    BusinessExpense: "Business expense",
    Calendar: "Calendar",
    CalendarEvent: "Calendar event",
    Case: "Matter",
    CaseParty: "Matter party",
    CaseStaff: "Matter staff",
    Child: "Child",
    Client: "Client profile",
    Clock: "Time entry",
    Company: "Company profile",
    Court: "Court",
    Debt: "Debt",
    Divorce: "Divorce profile",
    DivorceParty: "Divorce party",
    Email: "Email",
    Employee: "Employee profile",
    Expense: "Expense",
    File: "File",
    FinancialAccount: "Financial account",
    Folder: "Folder",
    Invoice: "Invoice",
    JournalEntry: "Accounting journal entry",
    JournalEntryLine: "Accounting journal line",
    Judge: "Judge profile",
    Lawyer: "Lawyer profile",
    Lead: "Lead profile",
    LeadActivity: "Lead activity",
    Log: "Activity log",
    Marriage: "Marriage profile",
    MarriageChild: "Marriage child",
    MarriageDebt: "Marriage debt",
    MarriageParty: "Marriage party",
    MarriageProperty: "Marriage property",
    NameChange: "Name change profile",
    Notification: "Notification",
    NotificationFile: "Notification file",
    Organization: "Organization",
    Party: "Legacy party",
    Payment: "Payment",
    PayPeriod: "Pay period",
    PayrollCheck: "Payroll check",
    PayrollCheckDeduction: "Payroll check deduction",
    PayrollCheckEarning: "Payroll check earning",
    PayrollCheckTax: "Payroll check tax",
    PaySchedule: "Pay schedule",
    Person: "Person profile",
    PersonFile: "Person file",
    PersonSetting: "Person setting",
    Property: "Property",
    Role: "Role",
    RolePerson: "Role assignment",
    Staff: "Staff profile",
    Survey: "Survey",
    SurveyRequest: "Survey request",
    Timecard: "Timecard",
    TrustLedger: "Trust ledger",
    Vendor: "Vendor profile",
    Workflow: "Workflow",
    WorkflowEvent: "Workflow event",
};

const FIELD_LABELS = {
    assignmentDate: "Assignment date",
    anchorDate: "Anchor date",
    birthDate: "Date of birth",
    birthPlaceUuid: "Birthplace",
    clearedAt: "Cleared date",
    driversLicenseNumber: "Driver's license number",
    driversLicenseState: "Driver's license state",
    due: "Due date",
    educationLevel: "Education level",
    ended: "End time",
    employerAddressUuid: "Employer address",
    employerName: "Employer name",
    employerPhone: "Employer phone",
    expended: "Expense date",
    finishBy: "Due by",
    genderCode: "Gender",
    generated: "Generated date",
    hireDate: "Hire date",
    intakeDate: "Intake date",
    lastActivityAt: "Last activity date",
    lastInvoiced: "Last invoice date",
    nextFollowUpAt: "Next follow-up date",
    openedAt: "Opened date",
    postedAt: "Posted date",
    ratesReviewed: "Rates reviewed date",
    reconciledAt: "Reconciled date",
    recorded: "Payment date",
    retainerAsOf: "Retainer as-of date",
    spouseCode: "Marital status",
    spouseUuid: "Spouse",
    ssn: "SSN",
    started: "Start time",
};

const MODEL_FIELD_LABELS = {
    BankReconciliationLine: {
        clearedAt: "Line cleared date",
    },
    BoardTask: {
        finishBy: "Task due date",
    },
    Clock: {
        ended: "Time entry end",
        started: "Time entry start",
    },
    JournalEntryLine: {
        clearedAt: "Journal line cleared date",
    },
    Payment: {
        postedAt: "Payment posted date",
        recorded: "Payment received date",
    },
};

const humanize = (value = "") => String(value)
    .replace(/Uuid$/i, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim()
    .replace(/^./, (letter) => letter.toUpperCase());

const modelLabel = (model) => MODEL_LABELS[model] || humanize(model);

const fieldLabel = (item) => MODEL_FIELD_LABELS[item.model]?.[item.field]
    || FIELD_LABELS[item.field]
    || humanize(item.field);

const summaryText = (summary = {}) => {
    const totals = Object.values(summary).reduce((memo, item) => ({
        create: memo.create + Number(item.create || 0),
        update: memo.update + Number(item.update || 0),
        unchanged: memo.unchanged + Number(item.unchanged || 0),
        skipped: memo.skipped + Number(item.skipped || 0),
    }), { create: 0, update: 0, unchanged: 0, skipped: 0 });

    return `${totals.create} new, ${totals.update} updated, ${totals.unchanged} unchanged, ${totals.skipped} skipped`;
};

function PersonImportModal({
    show,
    preview,
    resolutions,
    importing,
    onResolutionChange,
    onCancel,
    onApply,
}) {
    const conflicts = preview?.conflicts || [];
    const warnings = preview?.warnings || [];
    const person = preview?.person || {};
    const incoming = person.incoming || {};
    const match = person.match || null;

    return (
        <Modal size="xl" backdrop="static" keyboard show={show} onHide={onCancel}>
            <Modal.Header closeButton>
                <Modal.Title>Import Person Extract</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant={conflicts.length ? "warning" : "info"}>
                    <div>
                        {incoming.displayName || [incoming.firstName, incoming.lastName].filter(Boolean).join(" ") || incoming.email || incoming.uuid}
                    </div>
                    <div className="text-small">
                        {match
                            ? `Matched existing person by ${person.matchedBy}: ${match.displayName || match.email || match.uuid}`
                            : "No matching person was found. A new person will be created."}
                    </div>
                    <div className="text-small">{summaryText(preview?.summary)}</div>
                </Alert>

                {warnings.length > 0 && (
                    <Alert variant="warning">
                        {warnings.map((warning) => (
                            <div key={warning}>{warning}</div>
                        ))}
                    </Alert>
                )}

                {conflicts.length > 0 ? (
                    <Table responsive bordered hover size="sm" className="mb-0">
                        <thead>
                            <tr>
                                <th className="text-muted text-small text-uppercase">Field</th>
                                <th className="text-muted text-small text-uppercase">Current</th>
                                <th className="text-muted text-small text-uppercase">Imported</th>
                            </tr>
                        </thead>
                        <tbody>
                            {conflicts.map((item) => (
                                <tr key={item.id}>
                                    <td className="align-middle">
                                        <div>{fieldLabel(item)}</div>
                                        <div className="text-muted text-small">{modelLabel(item.model)}</div>
                                    </td>
                                    <td className="align-middle">
                                        <Form.Check
                                            type="radio"
                                            id={`${item.id}-current`}
                                            name={item.id}
                                            checked={(resolutions[item.id] || "current") === "current"}
                                            onChange={() => onResolutionChange(item.id, "current")}
                                            label={formatValue(item.current)}
                                        />
                                    </td>
                                    <td className="align-middle">
                                        <Form.Check
                                            type="radio"
                                            id={`${item.id}-incoming`}
                                            name={item.id}
                                            checked={resolutions[item.id] === "incoming"}
                                            onChange={() => onResolutionChange(item.id, "incoming")}
                                            label={formatValue(item.incoming)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <div className="text-muted">No conflicting values were found.</div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <ConditionalButton enabledVariant="outline-primary" display enabled={!importing} onEnabledClick={onCancel} icon="error-hexagon">cancel</ConditionalButton>
                <ConditionalButton enabledVariant="outline-primary" display enabled={!importing} onEnabledClick={onApply} icon="upload">import</ConditionalButton>
            </Modal.Footer>
        </Modal>
    );
}

export default PersonImportModal;
